import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtMoney, fmtErr } from "../api";
import { CheckCircle, LockSimple, XCircle } from "@phosphor-icons/react";
import { LegalLinks } from "../components/LegalLinks";
import { ServiceQuoteView } from "./PublicServiceQuote";
import { toast } from "sonner";

export default function QuoteLink() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/quote/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(e.response?.data?.detail || "This quote link isn't valid"));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, [token]);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api.post(`/public/quote/${token}/decide`, { approve });
      toast.success(approve ? "Quote approved — the operator has been notified" : "Quote declined");
      setQ((prev) => prev && { ...prev, status: r.data.status });
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const awaiting = q && ((q.kind === "job" && q.status === "quoted") || (q.kind === "instruction" && q.status === "awaiting_approval"));
  const accepted = q && ["approved", "scheduled", "in_progress", "completed", "invoiced", "paid"].includes(q.status);

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-8" data-testid="quote-link-page">
      <div className="w-full max-w-lg space-y-4">
        {q?.logo_url && <img src={q.logo_url} alt={q.business_name || ""} className="mx-auto max-h-14" data-testid="quote-link-logo" />}
        <p className="text-center font-heading text-xl font-bold" data-testid="quote-link-business">{q?.business_name || "Quote"}</p>

        {err && (
          <div className="card-flat space-y-2 p-8 text-center" data-testid="quote-link-error">
            <LockSimple size={40} weight="bold" className="mx-auto text-muted-foreground" />
            <p className="font-heading text-lg font-bold">Link unavailable</p>
            <p className="text-sm text-muted-foreground">{err}</p>
          </div>
        )}

        {!err && !q && <p className="text-center text-muted-foreground">Loading…</p>}

        {q && q.kind === "service_quote" && <ServiceQuoteView q={q} token={token} onChanged={load} />}
        {q && q.kind !== "service_quote" && (
          <div className="card-flat space-y-4 p-6" data-testid="quote-link-card">
            <p className="font-heading text-lg font-bold" data-testid="quote-link-title">{q.title}</p>
            {q.description && <p className="text-sm text-muted-foreground" data-testid="quote-link-desc">{q.description}</p>}
            {q.kind === "job" && q.line_items?.length > 0 && (
              <div className="space-y-1" data-testid="quote-link-lines">
                {q.line_items.map((l, i) => (
                  <div key={`${l.description}-${i}`} className="flex justify-between border-b border-border pb-1 text-sm">
                    <span>{l.description}{l.qty > 1 ? ` × ${l.qty}` : ""}</span>
                    <span className="font-semibold">{fmtMoney(l.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {q.kind === "job" && q.subtotal != null && q.tax ? (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(q.subtotal)}</span></div>
                <div className="flex justify-between"><span>{q.tax_label || "Tax"}</span><span>{fmtMoney(q.tax)}</span></div>
                <div className="flex justify-between font-heading text-lg font-bold" data-testid="quote-link-total"><span>Total</span><span>{fmtMoney(q.total)}</span></div>
              </div>
            ) : (
              <p className="font-heading text-2xl font-bold text-primary" data-testid="quote-link-total">{fmtMoney(q.total)}</p>
            )}
            {q.deposit_percent > 0 && (
              <p className="rounded-md bg-muted p-3 text-xs" data-testid="quote-link-deposit">
                A deposit of <strong>{fmtMoney(q.deposit_total)}</strong> ({q.deposit_percent}%) is invoiced on approval; the balance is billed on completion.
              </p>
            )}
            {awaiting && (
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="quote-approve-btn" onClick={() => decide(true)} disabled={busy}
                  className="h-14 rounded-md bg-primary font-bold text-primary-foreground disabled:opacity-40">Approve</button>
                <button data-testid="quote-decline-btn" onClick={() => decide(false)} disabled={busy}
                  className="h-14 rounded-md border border-destructive font-bold text-destructive disabled:opacity-40">Decline</button>
              </div>
            )}
            {accepted && (
              <div className="space-y-2 text-center" data-testid="quote-link-approved">
                <CheckCircle size={40} weight="fill" className="mx-auto text-primary" />
                <p className="font-heading text-lg font-bold">Approved</p>
                <p className="text-sm text-muted-foreground">This quote was approved — work will be scheduled.</p>
              </div>
            )}
            {(q.status === "lost" || q.status === "declined") && (
              <div className="space-y-2 text-center" data-testid="quote-link-declined">
                <XCircle size={40} weight="bold" className="mx-auto text-muted-foreground" />
                <p className="font-heading text-lg font-bold">Declined</p>
                <p className="text-sm text-muted-foreground">This quote was declined.</p>
              </div>
            )}
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground">Sent by {q?.business_name || "your operator"} via RPM Field (Veloktify)</p>
        <LegalLinks />
      </div>
    </div>
  );
}
